/**
 * If this directive is used, all children of the directive will be scanned for the class .sd-sticky-header.
 * Those found will be bucketed into groups based on how deep they are in the DOM hierarchy.
 * i.e. all headers that are have the same DOM distance from the the sticky directive will make one bucket.
 * In the below example div1, div2, and div6 will all be in one bucket. div4, div5, and div8 will all be in a second bucket.
 *      sd-sticky
 *          div1 class="sd-sticky-header"
 *          div2 class="sd-sticky-header"
 *          div3
 *              div4 class="sd-sticky-header"
 *              div5 class="sd-sticky-header"
 *          div6 class="sd-sticky-header"
 *          div7
 *              div8 class="sd-sticky-header"
 *              div9
 * For each bucket (starting with the depth closest to the container), if there is a header which is scrolled out of view,
 * then the header closest to the top will get stuck to the top. Then each depth below that will be considered
 * as it's own level of stickiness.
 */
const stuckHeaderClass = "sd-sticky-header-fixed";
// @ngInject
export let stickyDirective = function ($window: ng.IWindowService, $timeout: ng.ITimeoutService) {
  return {
    restrict: "A",
    link: function (scope: ng.IScope, element: ng.IAugmentedJQuery, attributes: ng.IAttributes) {
      let scrollContainer = element;
      let lastSeenWindowScroll: number;
      let lastSeenInnerScroll: number;
      let headerAtDepth: JQuery[] = [];
      let referenceHeaderAtDepth: HTMLElement[] = [];
      let options: { onSdStickyChange: (header: JQuery) => void };
      let distanceFromReferenceHeaderMap = new Map<HTMLElement, number>();

      parseAttributes(attributes);

      /**
       * Returns elements with matching .sd-sticky-header class by depth from container
       * @param container the container element to search in
       */
      function getStickyElementData(container: ng.IAugmentedJQuery) {
        let allSticky = container.find(".sd-sticky-header");
        let depthMap = new Map<number, JQuery[]>();
        // grouping headers together based on depth.
        allSticky.each((index: number, elem: HTMLElement) => {
          let elemAsJquery = $(elem);
          if (!elemAsJquery.hasClass(stuckHeaderClass)) {
            let depth = getDepthFromAncestor(container, elemAsJquery);
            if (!depthMap.has(depth)) {
              depthMap.set(depth, []);
            }
            depthMap.get(depth).push(elemAsJquery);
          }
        });
        // create array from map where each index contains the next depth of JQueryElements
        let depthArray: JQuery[][] = [];
        let depth = 0;
        Array.from(depthMap.keys())
          .sort()
          .forEach((depthKey) => {
            depthArray.push(depthMap.get(depthKey));
            depth++;
          });

        return depthArray;
      }

      /**
       * Will determine the number of parents between the ancestor
       * and descendant.
       * @param ancestor an ancestor element
       * @param descendant some descendant of any ancestor
       */
      function getDepthFromAncestor(ancestor: JQuery, descendant: JQuery) {
        let depth = 0;
        while (descendant.parent()[0] != ancestor[0]) {
          descendant = descendant.parent();
          depth++;
        }
        return depth;
      }

      function getNumberFromStyle(style: string) {
        return parseInt(style);
      }

      function getTopPositionFromWindow(element: JQuery, useParentOffset?: boolean) {
        if (useParentOffset) {
          // find the first parent which has a scroll
          let parent = element.parent();
          while (parent) {
            if (parent.scrollTop() != 0) {
              break;
            }
            parent = parent.parent();
          }
          if (parent && parent.offset()) {
            return parent.offset().top - $window.scrollY;
          }
        }
        return element.offset().top - $window.scrollY;
      }

      function getOffsetTopFromParent(element: JQuery) {
        return getTopPositionFromWindow(element) - element.outerHeight();
      }

      /**
       * Gets the closest element to scrollPos which is above scrollPos.
       * @param stickyElements elements to be considered as headers
       * @param scrollPos the top most Y position of the container
       */
      function getStickyHeader(stickyElements: JQuery[], scrollPos: number): JQuery {
        let closest: JQuery;
        let closestOffset: number;
        let referenceHeaderPosition = getOffsetTopFromParent(stickyElements[0]);
        for (let i = 0; i < stickyElements.length; i++) {
          let stickyElement = stickyElements[i];
          let offsetTop: number;
          // ScrollPos is at the top, but any element that scrolls passed the current header should then become the new header.
          // the existing header is assumed to have the same height as the existing one, thus '- stickyElement.outerHeight'.
          if (distanceFromReferenceHeaderMap.has(stickyElement[0])) {
            offsetTop = distanceFromReferenceHeaderMap.get(stickyElement[0]) + referenceHeaderPosition;
          } else {
            offsetTop = getOffsetTopFromParent(stickyElement);
            distanceFromReferenceHeaderMap.set(stickyElement[0], offsetTop - referenceHeaderPosition);
          }
          if (offsetTop < scrollPos) {
            if (!closest || offsetTop > closestOffset) {
              closest = stickyElements[i];
              closestOffset = offsetTop;
            }
          } else {
            // they're sorted so return early.
            return closest;
          }
        }
        return closest;
      }

      /**
       * Evaluates directive attribute for additional options.
       * @param attributes Normalized list of attributes for the directive
       */
      function parseAttributes(attributes: ng.IAttributes): void {
        if (!attributes) {
          return;
        }
        let expression = attributes.sdSticky;
        if (expression) {
          options = scope.$eval(expression);
        }
      }

      /**
       * Copy the header object and set the copy as fixed at scrollPos
       * NOTE: Since we're copying the header, it means that if there are writes to the header objects as you scroll (i.e. scope updates),
       * those writes will NOT make it to the copied header.
       *
       * One way to fix this is to perhaps move the DOM element out of the page, however it has to be in a container of a
       * set size so the DOM isn't reshuffled every time you pull an item out and put one back in.
       * @param header The header object to copy and fix to the top
       * @param scrollPos Scroll position of the container
       * @param totalDepth total levels of header depth.
       * @param depth depth of the current header
       */
      function setHeaderPosition(header: JQuery, scrollPos: number, totalDepth: number, depth: number, force: boolean) {
        if (referenceHeaderAtDepth[depth] == header[0] && !force) {
          // no need to modify css if it hasn't moved.
          if (scrollPos + "px" != headerAtDepth[depth].css("top")) {
            headerAtDepth[depth].css({
              top: scrollPos + "px",
              position: "fixed",
              zIndex: "" + (totalDepth - depth),
              width: header.outerWidth() + "px"
            });
          }
          return;
        }
        referenceHeaderAtDepth[depth] = header[0];
        if (headerAtDepth[depth]) {
          headerAtDepth[depth].remove();
        }
        let copiedElement = $(angular.copy(header[0]));
        copiedElement.addClass("sd-sticky-header-fixed");
        copiedElement.css({
          top: scrollPos + "px",
          position: "fixed",
          zIndex: "" + (totalDepth - depth),
          width: header.outerWidth() + "px"
        });
        header.parent().append(copiedElement);
        headerAtDepth[depth] = copiedElement;
        let headerUtcDate = $(header).attr("eventUtcDate");
        if (options && options.onSdStickyChange) {
          options.onSdStickyChange(header);
        }
      }
      let queuedScrollProcess: boolean;
      let onScroll = (force?: boolean) => {
        if (!queuedScrollProcess) {
          queuedScrollProcess = true;
          $timeout(50, false).then(() => {
            let scrollPos = getTopPositionFromWindow(scrollContainer, true);
            let innerScroll = getTopPositionFromWindow(scrollContainer.children().first());
            // updating styles can cause a scroll event, but don't reprocess if the scroll position
            // is the same as what we last processed.
            if (scrollPos == lastSeenWindowScroll && innerScroll == lastSeenInnerScroll && !force) {
              return;
            }
            lastSeenWindowScroll = scrollPos;
            lastSeenInnerScroll = innerScroll;
            let stickyElementByDepth = getStickyElementData(scrollContainer);
            let depth = 0;
            let stickyElements = stickyElementByDepth[depth];
            while (stickyElements && stickyElements.length > 0) {
              let header = getStickyHeader(stickyElements, scrollPos) || stickyElements[0];
              setHeaderPosition(header, scrollPos, stickyElementByDepth.length, depth, force);
              stickyElements = stickyElementByDepth[++depth];
              scrollPos += header.outerHeight();
            }
            queuedScrollProcess = false;
          });
        }
      };

      let resizeListener = () => {
        onScroll(true);
      };
      $window.addEventListener("resize", resizeListener, true);
      $window.addEventListener("scroll", <() => void>onScroll, true);

      scope.$watch(
        () => {
          return getTopPositionFromWindow(scrollContainer, true);
        },
        () => {
          onScroll();
        }
      );

      scope.$on("$destroy", () => {
        $window.removeEventListener("scroll", <() => void>onScroll, true);
        $window.removeEventListener("resize", resizeListener, true);
      });
      scrollContainer.on("scroll", onScroll.bind(undefined));

      return onScroll;
    }
  };
};
